import { Legacy } from '@generated/Foundation.GMI.model';
import graphqlClientFactory from 'lib/graphql-client-factory';
// Update the import path below to the correct location of your ContentGrid GraphQL query and types
import ContentGridQuery, {
  ContentGridQueryResult,
} from 'components/authorable/shared/card-listing/ContentGrid/ContentGrid.graphql';
import TimedReleaseOverrideQuery, {
  TimedReleaseOverrideQueryResult,
} from 'components/authorable/shared/card-listing/ContentGrid/TimedReleaseOverride.graphql';
import {
  OptionalSearchParams,
  RelatedSearchParams,
  RelatedContentType,
  SortField,
  SortOrder,
  getSearch,
} from 'lib/services/content-service';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { ContentType } from 'lib/services/models/types';
import { ComponentRendering, Item, LayoutServiceData } from '@sitecore-content-sdk/nextjs';
import { SearchResponse } from 'lib/services/models/search-response';
import {
  ContentOverride,
  TimedReleaseOverride,
  normalizeStandardImageUrl,
} from 'components/authorable/shared/card-listing/CardListingShared';
import { BaseContentResponse } from 'lib/services/models/content-response';
import { getGmiSiteId } from 'lib/utils/sitecore/site-settings-utils';
import { getSearchConfigurationId } from 'lib/utils/sitecore/site-settings-utils';
import { getEnumValue } from 'lib/utils/sitecore/enum';

export const DEFAULT_CARD_FALLBACK_ASPECT_RATIO: ImageAspectRatio = '3:2';
export const CATEGORY_TILES_DEFAULT_ASPECT_RATIO: ImageAspectRatio = '1:1';

export const CARD_FALLBACK_IMAGE_PATHS: Record<ImageAspectRatio, string> = {
  '1:1': '/-/media/Project/GMI/Shared/Fallback-Images/fallback_image-1_1.png',
  '3:2': '/-/media/Project/GMI/Shared/Fallback-Images/fallback_image-3_2.png',
  '16:9': '/-/media/Project/GMI/Shared/Fallback-Images/fallback_image-16_9.png',
  '9:16': '/-/media/Project/GMI/Shared/Fallback-Images/fallback_image-9_16.png',
  '2:1': '/-/media/Project/GMI/Shared/Fallback-Images/fallback_image-3_1.png',
  '4:3': '/-/media/Project/GMI/Shared/Fallback-Images/fallback_image-5_4.png',
  '7:10': '/-/media/Project/GMI/Shared/Fallback-Images/fallback_image-7_10.png',
};

/**
 * Returns true when `url` is a raw Sitecore item path rather than a routable
 * page URL (e.g. "/Components/…", "/sitecore/…", "/Data/…").
 * These paths exist only in the CMS tree and must never be used as navigation
 * hrefs; doing so causes Next.js to prefetch them as pages and triggers
 * "No content item for path" errors.
 */
export function isSitecoreItemPath(url: string | null | undefined): boolean {
  if (!url) return false;
  const normalized = url.toLowerCase();
  return (
    normalized.startsWith('/components/') ||
    normalized.startsWith('/sitecore/') ||
    normalized.startsWith('/data/') ||
    normalized.startsWith('/global/')
  );
}

export type ContentListWithOverrides = {
  result: SearchResponse | null;
  contentOverrides: Record<string, ContentOverride>;
  timedReleaseOverrides?: Record<string, TimedReleaseOverride>;
  fallbackImageUrl?: string;
};

export type ContentListStaticPropsOptions = {
  maxCards?: number;
  withFacets?: boolean;
  gridAspectRatio?: ImageAspectRatio;
  searchConfigurationId?: string;
};
// Shared static props fetcher for ContentGrid and ThreePackCards
export async function getContentListWithOverrides<TFields extends { contentList?: Item }>(
  rendering: ComponentRendering<TFields>,
  layoutData: LayoutServiceData,
  {
    maxCards,
    withFacets,
    gridAspectRatio,
    searchConfigurationId,
  }: ContentListStaticPropsOptions = {}
): Promise<ContentListWithOverrides | null> {
  const componentFields = rendering.fields as TFields;

  const contentList = componentFields?.contentList as Item | undefined;

  const sitecoreContext = layoutData?.sitecore?.context;

  const siteId = getGmiSiteId(sitecoreContext);

  const resolvedSearchConfigurationId =
    searchConfigurationId ?? getSearchConfigurationId(sitecoreContext);

  if (!contentList || !siteId) {
    return null;
  }

  const maxResults = maxCards || parseInt(rendering.params?.maxCards || '0') || undefined;

  const locale = sitecoreContext.language ?? 'en';

  const result = await getContentListSearchResult(
    siteId,
    locale,
    maxResults,
    contentList,
    sitecoreContext.contentItem as
      | import('lib/services/models/content-response').BaseContentResponse
      | undefined,
    withFacets ?? false,
    resolvedSearchConfigurationId
  );

  const contentOverrides = result
    ? await getContentListContentOverrides(contentList, locale)
    : null;

  const timedReleaseOverrides = result ? await getTimedReleaseOverrides(contentList, locale) : null;

  const fallbackImageUrl = await getContentListFallbackImageUrl({
    gridAspectRatio,
    result,
    contentOverrides,
  });

  const resultWithFallback = applyFallbackImageToSearchResult(result, fallbackImageUrl);
  const contentOverridesWithFallback = applyFallbackImageToOverrides(
    contentOverrides,
    fallbackImageUrl
  );

  return {
    result: resultWithFallback,
    contentOverrides: contentOverridesWithFallback || {},
    timedReleaseOverrides: timedReleaseOverrides || {},
    fallbackImageUrl,
  };
}

async function getContentListFallbackImageUrl({
  gridAspectRatio,
}: {
  gridAspectRatio?: ImageAspectRatio;
  result: SearchResponse | null;
  contentOverrides: Record<string, ContentOverride> | null;
}): Promise<string> {
  return getFallbackImagePathByAspectRatio(gridAspectRatio);
}

function getFallbackImagePathByAspectRatio(gridAspectRatio?: ImageAspectRatio): string {
  const resolvedAspectRatio = gridAspectRatio ?? DEFAULT_CARD_FALLBACK_ASPECT_RATIO;
  const path = CARD_FALLBACK_IMAGE_PATHS[resolvedAspectRatio];
  const cmHost = process.env.NEXT_PUBLIC_SITECORE_HOST ?? '';
  return cmHost ? `${cmHost}${path}` : path;
}

function applyFallbackImageToSearchResult(
  result: SearchResponse | null,
  fallbackImageUrl: string
): SearchResponse | null {
  if (!result || !fallbackImageUrl) {
    return result;
  }

  return {
    ...result,
    Results: result.Results.map((record) => ({
      ...record,
      StandardImageUrl:
        normalizeStandardImageUrl(record.StandardImageUrl, record.PageUrl) || fallbackImageUrl,
    })),
  };
}

function applyFallbackImageToOverrides(
  contentOverrides: Record<string, ContentOverride> | null,
  fallbackImageUrl: string
): Record<string, ContentOverride> | null {
  if (!contentOverrides || !fallbackImageUrl) {
    return contentOverrides;
  }

  return contentOverrides;
}

// Shared static props fetcher for ContentGrid and ThreePackCards
export async function getContentListSearchResult(
  siteId: string,
  locale: string | undefined,
  maxResults: number | undefined,
  contentList: Item | undefined,
  contentItem: BaseContentResponse | undefined,
  withFacets: boolean,
  searchConfigurationId: string | undefined
): Promise<SearchResponse | null> {
  const contentResult = parseContentListItem(contentList);
  if (!contentResult) {
    return null;
  }
  const {
    contentIds,
    metadataIds,
    contentType,
    relatedContentType,
    searchTerm,
    sortBy,
    sortDirection,
    matchAllTags,
  } = contentResult;

  // Make sure we don't pass 0 and pass undefined if no maxResults or contentIds.length
  const pageSize = maxResults || contentIds.length || undefined;
  const params: OptionalSearchParams | RelatedSearchParams = {
    language: locale,
    contentIds: contentIds,
    metadataIds: metadataIds,
    contentTypes: contentType ? [contentType as ContentType] : undefined,
    pageSize: pageSize,
    searchTerm: searchTerm,
    relatedContentType: relatedContentType,
    contentItem: contentItem,
    sortBy: sortBy,
    sortDirection: sortDirection,
    searchConfigurationId,
    facetGroups: [],
    getFacets: withFacets,
    taggedSearchMatchAll: matchAllTags,
  };

  const result = await getSearch(siteId, params);

  return result;
}

export interface ParsedContentListItem {
  contentIds: string[];
  metadataIds: string[];
  contentType: ContentType;
  relatedContentType?: RelatedContentType;
  searchTerm: string;
  sortBy: SortField;
  sortDirection: SortOrder;
  matchAllTags?: boolean;
}

export function parseContentListItem(contentList: Item | undefined): ParsedContentListItem | null {
  if (!contentList) {
    return null;
  }
  const contentItems = contentList as
    | Legacy.ContentDataSources.CuratedContentDataSource_Item
    | undefined;

  const dynamicContentItems = contentList as
    | Legacy.ContentDataSources.DynamicContentDataSource_Item
    | undefined;

  // If none of these fields exist, then the content list is not valid
  const hasFields =
    contentItems?.fields?.Items ||
    dynamicContentItems?.fields?.MetadataServiceTags ||
    dynamicContentItems?.fields?.contentType ||
    dynamicContentItems?.fields?.relatedContentType ||
    dynamicContentItems?.fields?.searchTerm ||
    dynamicContentItems?.fields?.defaultSortBy ||
    dynamicContentItems?.fields?.defaultSortDirection;

  if (!hasFields) {
    return null;
  }

  const contentIds = contentItems?.fields?.Items?.value?.split('|') || [];
  const metadataIds = dynamicContentItems?.fields?.MetadataServiceTags?.value?.split('|') || [];
  const contentType = dynamicContentItems?.fields?.contentType?.value;
  const relatedContentType = getEnumValue<RelatedContentType>(
    dynamicContentItems?.fields?.relatedContentType
  );
  const searchTerm = dynamicContentItems?.fields?.searchTerm?.value;
  const sortBy = dynamicContentItems?.fields?.defaultSortBy?.value || 'None';
  const sortDirection = dynamicContentItems?.fields?.defaultSortDirection?.value || 'None';
  const matchAllTags = dynamicContentItems?.fields?.MatchAllTags?.value;

  return {
    contentIds,
    metadataIds,
    contentType: (contentType as ContentType) || 'All',
    relatedContentType: relatedContentType,
    searchTerm: searchTerm ?? '',
    sortBy,
    sortDirection,
    matchAllTags,
  };
}

export async function getContentListContentOverrides(
  contentList: Item,
  locale: string
): Promise<Record<string, ContentOverride> | null> {
  const graphQLClient = graphqlClientFactory({});
  const gqlResult = await graphQLClient.request<ContentGridQueryResult>(ContentGridQuery, {
    datasource: contentList?.id,
    language: locale,
  });
  return (
    gqlResult.root?.children?.results
      ?.filter((result) => result.overrideContent?.jsonValue.value)
      .reduce(
        (acc, result) => {
          const rawHref = result.url?.jsonValue.value.href || '';
          // Guard: skip URLs that are raw Sitecore item paths (e.g. /Components/...).
          // These are datasource paths, not routable pages; using them as href
          // causes Next.js to prefetch them as pages, producing
          // "No content item for path" errors at runtime.
          const safeUrl = isSitecoreItemPath(rawHref) ? '' : rawHref;
          if (isSitecoreItemPath(rawHref)) {
            console.warn(
              `[ContentGrid] ContentOverride has a datasource item path as Url ("${rawHref}"). ` +
                'This is not a valid page route and will be ignored. ' +
                'Please update the Url field in Sitecore to point to a valid page.'
            );
          }
          acc[result.overrideContent?.jsonValue.value ?? ''] = {
            overrideContent: result.overrideContent?.jsonValue.value,
            title: result.title?.jsonValue.value,
            removeTitleLink: result.removeTitleLink?.jsonValue.value,
            image: result.image?.jsonValue.value?.src || '',
            tag: result.tag?.jsonValue.value,
            description: result.description?.jsonValue.value,
            url: safeUrl,
            ctaText: result.ctaText?.jsonValue.value,
          };
          return acc;
        },
        {} as Record<string, ContentOverride>
      ) || null
  );
}

/**
 * Fetches Timed Release Override items for a datasource, mirroring
 * {@link getContentListContentOverrides} but targeting the
 * `TimedReleaseOverride` template. Returns a map keyed by the overridden
 * ContentId. The additional `releaseDate` field is included so consumers can
 * time-gate when the override should take effect.
 */
export async function getTimedReleaseOverrides(
  contentList: Item,
  locale: string
): Promise<Record<string, TimedReleaseOverride> | null> {
  const graphQLClient = graphqlClientFactory({});
  const gqlResult = await graphQLClient.request<TimedReleaseOverrideQueryResult>(
    TimedReleaseOverrideQuery,
    {
      datasource: contentList?.id,
      language: locale,
    }
  );
  return (
    gqlResult.root?.children?.results
      ?.filter((result) => result.overrideContent?.jsonValue.value)
      .reduce(
        (acc, result) => {
          const rawHref = result.url?.jsonValue.value.href || '';
          // Guard: skip URLs that are raw Sitecore item paths (e.g. /Components/...).
          // These are datasource paths, not routable pages; using them as href
          // causes Next.js to prefetch them as pages, producing
          // "No content item for path" errors at runtime.
          const safeUrl = isSitecoreItemPath(rawHref) ? '' : rawHref;
          if (isSitecoreItemPath(rawHref)) {
            console.warn(
              `[TimedReleaseOverride] Override has a datasource item path as URL ("${rawHref}"). ` +
                'This is not a valid page route and will be ignored. ' +
                'Please update the URL field in Sitecore to point to a valid page.'
            );
          }
          acc[result.overrideContent?.jsonValue.value ?? ''] = {
            overrideContent: result.overrideContent?.jsonValue.value,
            title: result.title?.jsonValue.value,
            removeTitleLink: result.removeTitleLink?.jsonValue.value,
            image: result.image?.jsonValue.value?.src || '',
            tag: result.tag?.jsonValue.value,
            description: result.description?.jsonValue.value,
            url: safeUrl,
            ctaText: result.ctaText?.jsonValue.value,
            releaseDate: result.releaseDate?.jsonValue.value,
          };
          return acc;
        },
        {} as Record<string, TimedReleaseOverride>
      ) || null
  );
}
